// Appearance (light / dark / match device) for the main app.
// The preference is stored per device. public/index.html reads the same
// key before the bundle loads so the page never flashes white; keep the
// key and the class name in sync with that inline script.

export const THEME_STORAGE_KEY = 'hera-theme'
export const THEME_LIGHT = 'light'
export const THEME_DARK = 'dark'
export const THEME_SYSTEM = 'system'
export const THEME_PREFERENCES = [THEME_LIGHT, THEME_DARK, THEME_SYSTEM]
export const DEFAULT_THEME_PREFERENCE = THEME_LIGHT

export const DARK_CLASS = 'dark'
export const THEME_COLORS = { [THEME_LIGHT]: '#ffffff', [THEME_DARK]: '#121417' }

const DARK_QUERY = '(prefers-color-scheme: dark)'

export function normalizePreference(value) {
  return THEME_PREFERENCES.includes(value) ? value : DEFAULT_THEME_PREFERENCE
}

export function resolveTheme(preference, systemPrefersDark) {
  const pref = normalizePreference(preference)
  if (pref === THEME_SYSTEM) return systemPrefersDark ? THEME_DARK : THEME_LIGHT
  return pref
}

export function applyTheme(theme, doc) {
  const root = doc.documentElement
  const isDark = theme === THEME_DARK
  root.classList.toggle(DARK_CLASS, isDark)
  root.style.colorScheme = isDark ? 'dark' : 'light'
  const meta = doc.querySelector('meta[name="theme-color"]')
  if (meta) meta.setAttribute('content', THEME_COLORS[isDark ? THEME_DARK : THEME_LIGHT])
}

export function createThemeController(win) {
  const doc = win.document
  const media = typeof win.matchMedia === 'function' ? win.matchMedia(DARK_QUERY) : null
  let forceLight = false
  const listeners = new Set()
  let preference = DEFAULT_THEME_PREFERENCE
  try {
    preference = normalizePreference(win.localStorage.getItem(THEME_STORAGE_KEY))
  } catch (e) {
    // Storage blocked: fall back to the default.
  }

  function readPreference() {
    return preference
  }

  function currentTheme() {
    if (forceLight) return THEME_LIGHT
    return resolveTheme(readPreference(), !!(media && media.matches))
  }

  function refresh() {
    const theme = currentTheme()
    applyTheme(theme, doc)
    listeners.forEach(fn => fn(theme))
  }

  if (media) {
    const onChange = () => { if (readPreference() === THEME_SYSTEM) refresh() }
    if (typeof media.addEventListener === 'function') media.addEventListener('change', onChange)
    else if (typeof media.addListener === 'function') media.addListener(onChange) // Safari < 14
  }

  // Printed pages always use the light palette, whatever is on screen.
  if (typeof win.addEventListener === 'function') {
    win.addEventListener('beforeprint', () => applyTheme(THEME_LIGHT, doc))
    win.addEventListener('afterprint', () => applyTheme(currentTheme(), doc))
    // Keep other open tabs in step when the setting changes.
    win.addEventListener('storage', (event) => {
      if (event.key !== THEME_STORAGE_KEY) return
      preference = normalizePreference(event.newValue)
      refresh()
    })
  }

  return {
    getPreference: readPreference,
    getTheme: currentTheme,
    setPreference(value) {
      const pref = normalizePreference(value)
      preference = pref
      try {
        win.localStorage.setItem(THEME_STORAGE_KEY, pref)
      } catch (e) {
        // Storage can be blocked (private mode); the choice still applies for this session.
      }
      refresh()
      return pref
    },
    setForceLight(value) {
      forceLight = !!value
      refresh()
    },
    onChange(fn) {
      listeners.add(fn)
      return () => listeners.delete(fn)
    },
    refresh,
  }
}

let controller = null

export function getThemeController() {
  if (!controller) controller = createThemeController(window)
  return controller
}
